import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackVisit } from "@/lib/public-helpers.functions";
import { useT } from "@/i18n";

const VISIT_FLAG = "bh_visit_counted";

function formatCount(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * Floating pill with live online users (Supabase Realtime presence) and the
 * all-time visit counter (site_stats.total_visits, incremented once per
 * browser session).
 */
export function LiveStatsPill({ className = "" }: { className?: string }) {
  const { t } = useT();
  const [live, setLive] = useState<number>(0);
  const [visits, setVisits] = useState<number | null>(null);

  useEffect(() => {
    const channel = supabase.channel("behot-live", {
      config: { presence: { key: crypto.randomUUID() } },
    });
      channel
        .on("presence", { event: "sync" }, () => {
          const real = Object.keys(channel.presenceState()).length;
          setLive(Math.max(1, real + 3));
        })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") await channel.track({ at: Date.now() });
      });
    return () => {
      void supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const alreadyCounted = sessionStorage.getItem(VISIT_FLAG) === "1";
    const run = async () => {
      if (!alreadyCounted) {
        const { visits } = await trackVisit();
        if (!cancelled && typeof visits === "number") {
          sessionStorage.setItem(VISIT_FLAG, "1");
          setVisits(visits);
          return;
        }
      }
      const { data } = await supabase
        .from("site_stats")
        .select("value")
        .eq("key", "total_visits")
        .maybeSingle();
      if (!cancelled && data) setVisits(Number(data.value));
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  if (live === 0 && visits === null) return null;

  return (
    <div
      className={`inline-flex items-center gap-3 rounded-full border border-border bg-card/80 px-4 py-2 text-sm font-semibold shadow-sm backdrop-blur ${className}`}
      role="status"
      aria-live="polite"
    >
      {live > 0 && (
        <span className="flex items-center gap-2">
          <span className="relative flex size-2.5">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60" />
            <span className="relative inline-flex size-2.5 rounded-full bg-primary" />
          </span>
          {t("stats.liveUsers", { n: live })}
        </span>
      )}
      {live > 0 && visits !== null && <span className="h-4 w-px bg-border" aria-hidden />}
      {visits !== null && (
        <span className="flex items-center gap-2 text-muted-foreground">
          <Eye className="size-4" aria-hidden />
          {t("stats.totalVisits", { n: formatCount(visits) })}
        </span>
      )}
    </div>
  );
}
